import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  requestNafathVerification,
  checkNafathStatus,
} from "@/services/api";
import type { NafathIdentityData } from "@/services/api";
import nafathLogo from "@/assets/landing/nafath-nic.png";
import { toast } from "sonner";

interface NafathVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nationalId?: string;
  userId?: string;
  companyId?: string;
  onVerified: (identity: NafathIdentityData | null, nationalId: string) => void;
}

type Step = "input" | "polling" | "success";

export function NafathVerificationModal({
  open,
  onOpenChange,
  nationalId: initialNationalId,
  userId,
  companyId,
  onVerified,
}: NafathVerificationModalProps) {
  const { t, language } = useLanguage();

  const [step, setStep] = useState<Step>("input");
  const [nationalId, setNationalId] = useState(initialNationalId || "");
  const [isRequesting, setIsRequesting] = useState(false);
  const [nafathCode, setNafathCode] = useState("");
  const [transId, setTransId] = useState("");
  const [countdown, setCountdown] = useState(120);
  const [identity, setIdentity] = useState<NafathIdentityData | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef = useRef(false);

  // Sync initial national ID when prop changes
  useEffect(() => {
    if (initialNationalId) {
      setNationalId(initialNationalId);
    }
  }, [initialNationalId]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("input");
      setNationalId(initialNationalId || "");
      setNafathCode("");
      setTransId("");
      setCountdown(120);
      setIdentity(null);
      doneRef.current = false;
    } else {
      stopPolling();
      stopCountdown();
    }
  }, [open, initialNationalId]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleRequestVerification = async () => {
    if (!nationalId || nationalId.length < 10) {
      toast.error(
        t("رقم الهوية يجب أن يكون 10 أرقام", "National ID must be 10 digits")
      );
      return;
    }

    setIsRequesting(true);
    try {
      const result = await requestNafathVerification(nationalId, userId, companyId);

      if (result.success && result.transId && result.random) {
        setTransId(result.transId);
        setNafathCode(result.random);
        setStep("polling");
        doneRef.current = false;
        startPolling(nationalId, result.transId, result.random);
      } else {
        toast.error(
          result.message ||
            t("فشل في طلب التحقق", "Failed to request verification")
        );
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : String(error);
      toast.error(message);
    } finally {
      setIsRequesting(false);
    }
  };

  const startPolling = (nid: string, tid: string, code: string) => {
    setCountdown(120);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopPolling();
          stopCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    pollingRef.current = setInterval(async () => {
      if (doneRef.current) return;
      try {
        const result = await checkNafathStatus(nid, tid, code);

        if (result.success && result.status === "COMPLETED") {
          doneRef.current = true;
          stopPolling();
          stopCountdown();
          handleVerificationComplete(result.data || null);
        } else if (result.status === "COMPLETED_PENDING_IDENTITY") {
          // Nafath confirmed the approval but the identity (and thus the NAME)
          // never landed — the late/dropped-callback race. NEVER treat this as
          // verified: send the user back to retry so the name can arrive,
          // instead of stamping a nameless verification.
          doneRef.current = true;
          stopPolling();
          stopCountdown();
          toast.error(
            t(
              "تعذّر جلب بياناتك من نفاذ، يرجى إعادة المحاولة",
              "Could not retrieve your details from Nafath, please try again"
            )
          );
          setStep("input");
        } else if (
          result.status === "REJECTED" ||
          result.status === "EXPIRED"
        ) {
          doneRef.current = true;
          stopPolling();
          stopCountdown();
          toast.error(
            result.status === "REJECTED"
              ? t("تم رفض التحقق", "Verification rejected")
              : t("انتهت صلاحية الرمز", "Code expired")
          );
          setStep("input");
        }
      } catch {
        // Silently retry on network errors
      }
    }, 3000);
  };

  const handleVerificationComplete = (data: NafathIdentityData | null) => {
    setIdentity(data);
    setStep("success");
  };

  const handleDone = () => {
    onVerified(identity, nationalId);
    onOpenChange(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <img src={nafathLogo} alt="Nafath" className="h-8 w-auto" />
            {t("التحقق عبر نفاذ", "Verify with Nafath")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("أدخل رقم هويتك الوطنية للتحقق من هويتك عبر تطبيق نفاذ", "Enter your national ID to verify your identity through the Nafath app")}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: National ID Input */}
        {step === "input" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t(
                "أدخل رقم هويتك الوطنية للتحقق عبر تطبيق نفاذ",
                "Enter your national ID to verify via the Nafath app"
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="nafath-national-id">
                {t("رقم الهوية", "National ID")}
              </Label>
              <Input
                id="nafath-national-id"
                value={nationalId}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length <= 10) setNationalId(val);
                }}
                placeholder="1xxxxxxxxx"
                dir="ltr"
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>
            <Button
              onClick={handleRequestVerification}
              disabled={isRequesting || !nationalId}
              className="w-full"
            >
              {isRequesting ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t("جاري الطلب...", "Requesting...")}
                </>
              ) : (
                t("تحقق الآن", "Verify Now")
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Nafath Code + Polling */}
        {step === "polling" && (
          <div className="space-y-6 py-2">
            <div className="text-center space-y-3">
              <img
                src={nafathLogo}
                alt="Nafath"
                className="h-16 w-auto mx-auto"
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  "افتح تطبيق نفاذ واختر الرقم التالي للتحقق",
                  "Open the Nafath app and select the following number to verify"
                )}
              </p>
            </div>

            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-2xl bg-primary/10 border-4 border-primary flex items-center justify-center">
                <span className="text-4xl font-bold text-primary">
                  {nafathCode}
                </span>
              </div>
            </div>

            {countdown > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">
                    {t("في انتظار التحقق...", "Waiting for verification...")}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {language === "ar"
                    ? `ينتهي خلال ${formatTime(countdown)}`
                    : `Expires in ${formatTime(countdown)}`}
                </span>
              </div>
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-destructive">
                  {t("انتهت صلاحية الرمز", "Code expired")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setStep("input")}
                  className="w-full"
                >
                  {t("إعادة المحاولة", "Try Again")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Success */}
        {step === "success" && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center gap-3 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
              <h3 className="text-lg font-semibold text-green-700 dark:text-green-400">
                {t("تم التحقق بنجاح", "Verification Successful")}
              </h3>
              {identity && (
                <p className="text-sm text-muted-foreground">
                  {language === "ar"
                    ? `${identity.firstName || ""} ${identity.secondName || ""} ${identity.lastName || ""}`
                    : `${identity.englishFirstName || ""} ${identity.englishSecondName || ""} ${identity.englishLastName || ""}`}
                </p>
              )}
            </div>
            <Button onClick={handleDone} className="w-full">
              {t("تم", "Done")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
